import type { ReactNode } from 'react';
import { Mic2, UserRound } from 'lucide-react';
import { motion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  ConclaveTableMateCard,
  type ConclaveTableMate,
} from '@/components/conclave/ConclaveTableMateCard';

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function ConclaveCurrentSpeakerBanner({
  speakerName,
  speakerImage,
  intro,
  mate,
  onGive,
  onAsk,
  onNote,
  onLead,
  speakingClock,
}: {
  speakerName?: string | null;
  speakerImage?: string | null;
  intro?: { title?: string | null; body?: string | null } | null;
  mate?: ConclaveTableMate | null;
  speakingClock?: ReactNode;
  onGive?: (mate: ConclaveTableMate) => void;
  onAsk?: (mate: ConclaveTableMate) => void;
  onNote?: (mate: ConclaveTableMate) => void;
  onLead?: (mate: ConclaveTableMate) => void;
}) {
  if (!intro?.title && !intro?.body && !speakerName && !mate) return null;

  if (mate) {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
        <ConclaveTableMateCard
          mate={mate}
          isSpeaking
          speakingIntro={intro}
          speakingClock={speakingClock}
          onGive={onGive}
          onAsk={onAsk}
          onNote={onNote}
          onLead={onLead}
        />
      </motion.div>
    );
  }

  const displayName = speakerName?.trim() || 'Someone at your table';
  const title = intro?.title?.trim() || 'Speaking now';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-4 overflow-hidden rounded-2xl border border-primary/30 bg-primary/10 shadow-sm"
    >
      <div className="flex gap-3 p-3 md:p-4">
        <div className="relative shrink-0">
          <span className="relative flex h-14 w-14 items-center justify-center md:h-16 md:w-16">
            <span
              aria-hidden
              className="animate-speaking-ring absolute inset-0 rounded-xl border-2 border-primary"
            />
            <span
              aria-hidden
              className="animate-speaking-ring-delayed absolute inset-0 rounded-xl border-2 border-primary/70"
            />
            <Avatar className="relative z-10 h-14 w-14 rounded-xl border-2 border-primary md:h-16 md:w-16">
              <AvatarImage src={speakerImage ?? undefined} alt={displayName} />
              <AvatarFallback className="rounded-xl bg-primary text-base font-semibold text-primary-foreground">
                {initials(displayName) || <UserRound className="h-6 w-6" />}
              </AvatarFallback>
            </Avatar>
          </span>
          <span className="absolute -bottom-1 -right-1 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
            <Mic2 className="h-3.5 w-3.5 animate-pulse" />
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="pt-1 text-[10px] font-bold uppercase tracking-widest text-primary">
              Current speaker
            </p>
            {speakingClock}
          </div>
          <p className="truncate text-lg font-semibold text-foreground">{displayName}</p>
          <p className="mt-0.5 font-medium text-foreground/90">{title}</p>
          {intro?.body ? (
            <p className="mt-2 line-clamp-4 text-sm leading-relaxed text-muted-foreground">
              {intro.body}
            </p>
          ) : null}
        </div>
      </div>
    </motion.div>
  );
}
